/**
 * FeatureGate — wraps any premium feature.
 * If the current driver user doesn't have the feature in their tier,
 * the content is blurred and an upgrade prompt overlays it.
 *
 * Usage:
 *   <FeatureGate featureKey="can_use_esignatures" featureName="E-Signatures">
 *     <YourPremiumComponent />
 *   </FeatureGate>
 *
 * Props:
 *  featureKey   - key in tier_features (e.g. "can_see_analytics")
 *  featureName  - human-readable name shown in the upgrade prompt
 *  description  - optional short description of what the feature does
 *  children     - the component to gate
 *  alwaysShow   - if true, don't gate (e.g. for admins viewing content)
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Lock, ArrowRight, Star, Sparkles } from 'lucide-react';
import { useAuth } from '../App';
import { BACKEND_URL } from '../config/api';

// ─── Permissions Context ──────────────────────────────────────────────────────

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [perms, setPerms] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPerms = useCallback(async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token || !user) { setLoading(false); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/membership/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setPerms(await res.json());
    } catch (err) { console.warn("FeatureGate error:", err?.message); } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchPerms(); }, [fetchPerms]);

  return (
    <PermissionsContext.Provider value={{ perms, loading, refresh: fetchPerms }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext) || { perms: null, loading: false, refresh: () => {} };
}

// ─── FeatureGate Component ────────────────────────────────────────────────────

export default function FeatureGate({ featureKey, featureName, description, children, alwaysShow = false }) {
  const { user } = useAuth();
  const { perms, loading } = usePermissions();
  const [showModal, setShowModal] = useState(false);

  // Admins / master_admin always see everything
  const isAdmin = user?.role === 'master_admin' || user?.role === 'admin';

  if (alwaysShow || isAdmin || loading || !perms) return <>{children}</>;

  const hasFeature = perms?.permissions?.[featureKey] === true || perms?.is_god_mode === true;

  if (hasFeature) return <>{children}</>;

  // Blurred + locked overlay
  return (
    <>
      <div className="relative group" data-testid={`feature-gate-${featureKey}`}>
        {/* Blurred content */}
        <div
          className="select-none pointer-events-none"
          style={{ filter: 'blur(4px)', opacity: 0.5 }}
        >
          {children}
        </div>

        {/* Overlay */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/70 backdrop-blur-sm rounded-xl cursor-pointer z-10 gap-3"
          onClick={() => setShowModal(true)}
          data-testid={`upgrade-prompt-${featureKey}`}
        >
          <div className="flex items-center gap-2 bg-gray-800 border border-orange-500/50 rounded-full px-4 py-2 shadow-lg">
            <Lock className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-semibold text-white">
              {featureName || 'Premium Feature'}
            </span>
          </div>
          <button className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-sm font-semibold px-5 py-2 rounded-full shadow-lg transition-all">
            <Star className="w-4 h-4" />
            Upgrade to Unlock
            <ArrowRight className="w-4 h-4" />
          </button>
          {description && (
            <p className="text-xs text-gray-400 text-center max-w-xs px-4">{description}</p>
          )}
        </div>
      </div>

      {/* Upgrade Modal */}
      {showModal && (
        <UpgradeModal
          featureKey={featureKey}
          featureName={featureName}
          description={description}
          currentPlanName={perms?.plan_display_name}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

// ─── Upgrade Modal ────────────────────────────────────────────────────────────

function UpgradeModal({ featureKey, featureName, description, currentPlanName, onClose }) {
  const [options, setOptions] = useState(null);
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/membership/upgrade-options`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setOptions(d))
      .catch(() => {});
  }, [token]);

  const handleUpgrade = () => {
    onClose();
    // Navigate to the membership tab
    window.dispatchEvent(new CustomEvent('navigate-to-membership'));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <Sparkles className="w-5 h-5 text-orange-400" />
            </div>
            <h2 className="text-xl font-bold text-white">Unlock {featureName || 'This Feature'}</h2>
          </div>
          {description && <p className="text-gray-400 text-sm">{description}</p>}
          <p className="text-gray-500 text-xs mt-2">
            Current Plan: <span className="text-orange-400 font-medium">{currentPlanName || '—'}</span>
          </p>
        </div>

        {/* Upgrade Options */}
        <div className="p-6">
          {options ? (
            <>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                Upgrade to unlock
              </h3>
              <div className="space-y-3">
                {(options.upgrade_options || []).slice(0, 3).map(plan => {
                  const hasIt = (plan.tier_features || {})[featureKey] === true;
                  return (
                    <div
                      key={plan.name}
                      className={`flex items-center justify-between p-3 rounded-xl border ${
                        hasIt
                          ? 'border-orange-500/50 bg-orange-900/10'
                          : 'border-gray-700 bg-gray-800/50 opacity-60'
                      }`}
                    >
                      <div>
                        <p className="font-semibold text-white">{plan.display_name}</p>
                        <p className="text-xs text-gray-400">
                          {plan.price_monthly === 0 ? 'Free' : `$${plan.price_monthly}/mo`}
                        </p>
                      </div>
                      {hasIt ? (
                        <span className="flex items-center gap-1 text-xs text-green-400 font-medium">
                          <Star className="w-3 h-3" /> Includes this
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">Not included</span>
                      )}
                    </div>
                  );
                })}
                {(options.upgrade_options || []).length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">You're already on the highest plan!</p>
                )}
              </div>
            </>
          ) : (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 border-t border-gray-700 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">
            Not Now
          </button>
          <button
            onClick={handleUpgrade}
            className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold px-5 py-2 rounded-lg text-sm transition-all"
            data-testid="upgrade-now-btn"
          >
            View Plans <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
